import { useContext, useEffect, useRef, useState, FC } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import clsx from 'clsx';

import { NavContext } from 'Context/nav-context';
import { assetsPath, KEY } from 'Data/constants';
import styles from './user-account-bar.module.scss';

import { useIntl } from 'react-intl';
import { isAnonymous } from 'Utils/auth';

import { accountLinksNav } from '../../../../data/account-links';
import { formatLink } from '@/utils';

const favouriteImageDimensions = [27, 22];
const badImageDimensions = [30, 30];

const UserAccountBar: FC = () => {
  const [showDropdown, setShowDropdown] = useState(false);

  const [accountLinkIndex, setAccountLinkIndex] = useState(0);

  const {
    user: {
      ['userStatus']: [userStatus, setUserStatus],
      ['loginURL']: [loginURL],
      ['bagCount']: [bagCount],
    },
    navId,
  } = useContext(NavContext);

  const barRef = useRef(null);
  const myAccountRef: any = useRef(null);
  const subMenuRefs: any = useRef([]);

  const [isUserAnonymous, setIsUserAnonymous] = useState(true);

  useEffect(() => {
    if (barRef.current && navId) {
      const domUserStatus = document.getElementById(navId)?.dataset?.userstatus;
      if (domUserStatus && userStatus !== domUserStatus) {
        setUserStatus(domUserStatus);
      }
    }
  }, [setUserStatus, userStatus, navId]);

  useEffect(() => {
    setIsUserAnonymous(isAnonymous());
  }, []);

  const { formatMessage } = useIntl();

  const { loading, error, data } = {
    loading: '',
    error: '',
    data: accountLinksNav,
  };

  if (loading) return null; // note: display text feedback isn't part of the design so we don't want anything to appear but fail silently
  if (error) return null;

  const accountLinks: any = accountLinksNav;

  const handleOnLinkClick = (link: string) => {
    if (link !== '/logout') return;
  };

  return (
    <div className={styles.lvl1UserAccountNav} ref={barRef}>
      <ul
        className={styles.userAccountNavContainer}
        role="menubar"
        aria-label={formatMessage({ id: isUserAnonymous ? 'signIn' : 'myAccount' })}
      >
        <li className={clsx(showDropdown && styles.selectedNav)} role="none">
          <a
            data-testid="signin"
            className={styles.navSignIn}
            href={formatLink(loginURL)}
            onClick={(e) => {
              if (!isUserAnonymous) e.preventDefault();
            }}
            onMouseEnter={() => {
              setShowDropdown(true);
            }}
            ref={myAccountRef}
            onKeyDown={(e) => {
              if (e.key == KEY.ENTER || e.key == KEY.SPACE || e.key == ' ') {
                if (!isUserAnonymous) e.preventDefault();
                if (!showDropdown) {
                  setTimeout(() => {
                    subMenuRefs.current[0].focus();
                  }, 0);
                }
                setShowDropdown(!showDropdown);
              }
            }}
            onMouseLeave={(e: any) => {
              const relatedTarget = e.relatedTarget?.parentNode?.className;
              if (!relatedTarget?.includes('userAccountNavContainer')) setShowDropdown(false);
            }}
            role="menuitem"
            aria-haspopup="true"
            aria-expanded="false"
          >
            {formatMessage({ id: isUserAnonymous ? 'signIn' : 'myAccount' })}
          </a>
        </li>
        <li role="none">
          <a data-testid="favourite" href={formatLink('/favourite')} className={styles.favoritesLink} role="menuitem">
            <Image
              src={`${assetsPath}/heart.svg`}
              alt={formatMessage({ id: 'favourite' })}
              aria-label={formatMessage({ id: 'favourite' })}
              width={favouriteImageDimensions[0]}
              height={favouriteImageDimensions[1]}
            />
            <span>{formatMessage({ id: 'favorites' })}</span>
          </a>
        </li>
        <li role="none">
          <Link data-testid="shoppingbag" href={formatLink('/shoppingbag')} role="menuitem">
            <Image
              src={`${assetsPath}/site-media/shopping-bag--black.svg`}
              alt={formatMessage({ id: 'bag' })}
              aria-label={formatMessage({ id: 'bag' })}
              width={badImageDimensions[0]}
              height={badImageDimensions[1]}
            />
            <span className={styles.shoppingBag} aria-label={formatMessage({ id: 'bag' })}>
              {formatMessage({ id: 'bag' })} (<span id="js-cart-item-count-nav-sub">{bagCount}</span>)
            </span>
          </Link>
        </li>
      </ul>
      <div
        className={styles.signinDropdown}
        style={!isUserAnonymous && showDropdown ? { display: 'flex' } : { display: 'none' }}
        onMouseLeave={() => {
          setShowDropdown(false);
        }}
      >
        <ul
          role="menubar"
          aria-label={formatMessage({ id: 'myAccount' })}
          onKeyDown={(e) => {
            if (e.key == KEY.ARROW_DOWN || e.key == KEY.SPACE || e.key == ' ') {
              e.preventDefault();
              const newAccountLinkIndex =
                accountLinkIndex + 1 >= accountLinks.length ? accountLinkIndex : accountLinkIndex + 1;
              subMenuRefs.current[newAccountLinkIndex].focus();
              setAccountLinkIndex(newAccountLinkIndex);
            }

            if (e.key == KEY.ARROW_UP) {
              e.preventDefault();
              const newAccountLinkIndex = accountLinkIndex - 1 < 0 ? 0 : accountLinkIndex - 1;
              subMenuRefs.current[newAccountLinkIndex].focus();
              setAccountLinkIndex(newAccountLinkIndex);
            }

            if (e.key == KEY.ESCAPE) {
              setShowDropdown(false);
              myAccountRef.current.focus();
            }
          }}
        >
          {accountLinks?.map(({ name, uid, link }: { name: string; uid: any; link: string }, index: any) => {
            return (
              <li key={uid} role="none">
                <a
                  data-testid={name}
                  href={formatLink(link)}
                  role="menuitem"
                  ref={(ref) => (subMenuRefs.current[index] = ref)}
                  tabIndex={0}
                  onClick={() => handleOnLinkClick(link)}
                >
                  {name}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default UserAccountBar;
